import type { App } from 'vue'
import type { Router } from 'vue-router'
import type { Workspace } from '@shared/domain/permissions'

/**
 * Errors go to Sentry with the correlation id and workspace, never with PII (spec §12).
 * Funnel events (wizard step completed, request submitted, submit failed) go to `track`.
 */
type Reporter = {
  captureException: (e: unknown, ctx?: Record<string, unknown>) => void
  addBreadcrumb: (b: { category: string; message: string; data?: Record<string, unknown> }) => void
  setTag: (k: string, v: string) => void
}

let reporter: Reporter = {
  captureException: (e, ctx) => console.error('[error]', e, ctx),
  addBreadcrumb: () => {},
  setTag: () => {},
}

export async function initObservability(
  app: App,
  router: Router,
  dsn: string,
  environment: string,
) {
  if (!dsn) return
  const Sentry = await import('@sentry/vue')
  Sentry.init({
    app,
    dsn,
    environment,
    integrations: [Sentry.browserTracingIntegration({ router })],
    tracesSampleRate: 0.1,
    beforeSend(event) {
      // strip anything user-identifying; keep the opaque user id only
      if (event.user) event.user = { id: event.user.id }
      if (event.request) {
        delete event.request.cookies
        delete event.request.headers
      }
      return event
    },
  })
  reporter = {
    captureException: (e, ctx) => Sentry.captureException(e, { extra: ctx }),
    addBreadcrumb: (b) => Sentry.addBreadcrumb(b),
    setTag: (k, v) => Sentry.setTag(k, v),
  }
}

export function setObservabilityContext(ctx: { workspace: Workspace | null; userId?: string }) {
  reporter.setTag('workspace', ctx.workspace ?? 'none')
}

export function captureError(e: unknown, ctx?: { correlationId?: string; [k: string]: unknown }) {
  reporter.captureException(e, ctx)
}

export type FunnelEvent =
  | 'wizard_step_completed'
  | 'wizard_submitted'
  | 'financing_request_submitted'
  | 'financing_submit_failed'

export function track(event: FunnelEvent, data: Record<string, string | number | boolean> = {}) {
  reporter.addBreadcrumb({ category: 'funnel', message: event, data })
  // eslint-disable-next-line no-console
  if (import.meta.env.DEV) console.info(`[track] ${event}`, data)
}
